import { FC, useEffect, useState } from 'react';

import { ExtraPaymentDto } from '@shared/api/giftCoupon/giftCoupon.controller';
import { GiftCouponSummary } from '@shared/api/giftCoupon/giftCoupon.interface';
import { ShareChannelIcon } from '@service/giftStore/components/ShareChannelIcon';

import {
  fmt,
  redeemStatusColor,
  redeemStatusLabel,
  shareChannelLabel,
  shareStatusColor,
  shareStatusLabel,
} from './couponDetail/constants';

// 스폰(추가교환) 쿠폰의 추가결제 대기건 처리 모달.
// 발송대기 탭(ExtraPaymentTab)과 주문 쿠폰상세 모달(CouponDetailModal)에서 공용으로 사용한다.

// ─── (A) 추가결제(입금) 모달 ───
export const ExtraPayModal: FC<{
  couponId: string;
  couponCode: string;
  productLabel: string;
  onPay: (couponId: string, dto: ExtraPaymentDto) => Promise<boolean>;
  onClose: () => void;
}> = ({ couponId, couponCode, productLabel, onPay, onClose }) => {
  const [method, setMethod] = useState('Card');
  const [amount, setAmount] = useState('');
  const [receiptId, setReceiptId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    const ok = await onPay(couponId, {
      method: method || undefined,
      amount: amount ? Number(amount) : undefined,
      receiptId: receiptId.trim() || undefined,
    });
    if (ok) onClose();
    else setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-sm space-y-4 rounded-xl bg-white p-5 shadow-2xl">
        <h3 className="text-base font-bold text-gray-800">추가결제 후 발송</h3>
        <p className="text-xs text-gray-500">
          {productLabel} · 코드 {couponCode}
        </p>
        <label className="block text-sm">
          <span className="font-medium text-gray-700">결제수단</span>
          <select
            value={method}
            onChange={(e) => setMethod(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
          >
            <option value="Card">카드</option>
            <option value="BankTransfer">입금</option>
          </select>
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700">금액 (선택)</span>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-gray-700">영수증 ID (선택)</span>
          <input
            type="text"
            value={receiptId}
            onChange={(e) => setReceiptId(e.target.value)}
            className="mt-1 w-full rounded-lg border px-3 py-2 text-sm"
          />
        </label>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? '처리중…' : '추가결제 확정'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── (B) 대체발송 모달 ───
export const ExtraFulfillModal: FC<{
  couponId: string;
  couponCode: string;
  productLabel: string;
  getDonors: (couponId: string) => Promise<GiftCouponSummary[]>;
  onFulfill: (couponId: string, donorCouponId: string) => Promise<boolean>;
  onClose: () => void;
}> = ({ couponId, couponCode, productLabel, getDonors, onFulfill, onClose }) => {
  const [donors, setDonors] = useState<GiftCouponSummary[] | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    getDonors(couponId)
      .then((res) => {
        if (!alive) return;
        setDonors(res);
        if (res[0]) setSelected(res[0]._id);
      })
      .catch((err) => {
        console.error('대체발송 후보 조회 실패:', err);
        if (alive) setDonors([]);
      });
    return () => {
      alive = false;
    };
  }, [couponId, getDonors]);

  const handleSubmit = async () => {
    if (!selected) return;
    setSubmitting(true);
    const ok = await onFulfill(couponId, selected);
    if (ok) onClose();
    else setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative flex max-h-[90vh] w-full max-w-4xl flex-col gap-4 rounded-xl bg-white p-5 shadow-2xl">
        <div>
          <h3 className="text-base font-bold text-gray-800">기구매 쿠폰으로 대체발송</h3>
          <p className="mt-0.5 text-xs text-gray-500">
            {productLabel} · 신청 {couponCode} — 충당할 기구매(미전달) 쿠폰을 고르세요.{' '}
            <span className="text-emerald-600">공유 이력이 없는(미공유·카톡 0회)</span>{' '}
            쿠폰이 안전합니다.
          </p>
        </div>

        {donors === null ? (
          <p className="py-10 text-center text-sm text-gray-500">후보 조회 중…</p>
        ) : donors.length === 0 ? (
          <p className="rounded-lg bg-amber-50 px-3 py-3 text-sm text-amber-700">
            대체 가능한 미전달 쿠폰이 없습니다. 추가결제로만 처리할 수 있습니다.
          </p>
        ) : (
          <div className="min-h-0 flex-1 overflow-auto rounded-md border">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 text-left text-gray-500">
                <tr>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">선택</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">#</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">구분</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">코드</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">교환상태</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유</th>
                  <th
                    className="whitespace-nowrap px-3 py-2 font-medium"
                    title="카카오톡 공유 웹훅으로 집계된 채팅방 수"
                  >
                    카톡공유
                  </th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">공유채널</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">구매일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">만료일</th>
                  <th className="whitespace-nowrap px-3 py-2 font-medium">메모</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {donors.map((d) => {
                  const isSel = selected === d._id;
                  const kakao = d.shareKakaoCount ?? 0;
                  return (
                    <tr
                      key={d._id}
                      onClick={() => setSelected(d._id)}
                      className={`cursor-pointer ${
                        isSel ? 'bg-emerald-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <input
                          type="radio"
                          name="donor"
                          checked={isSel}
                          onChange={() => setSelected(d._id)}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                        />
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-400">
                        {d.seq}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {d.isReissued ? (
                          <span className="rounded-full bg-teal-100 px-1.5 py-0.5 text-[10px] font-bold text-teal-700">
                            재발급
                          </span>
                        ) : d.isAdditional ? (
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                            재발행
                          </span>
                        ) : (
                          <span className="text-gray-300">일반</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 font-mono font-medium text-gray-800">
                        {d.code}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <span
                          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            redeemStatusColor[d.redeemStatus]
                          }`}
                        >
                          {redeemStatusLabel[d.redeemStatus]}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        <span
                          className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
                            shareStatusColor[d.shareStatus]
                          }`}
                        >
                          {shareStatusLabel[d.shareStatus]}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {kakao > 0 ? (
                          <span
                            className={`inline-flex rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                              kakao >= 2
                                ? 'bg-red-500 text-white'
                                : 'bg-kakao-yellow text-kakao-brown'
                            }`}
                            title={
                              kakao >= 2
                                ? `⚠ ${kakao}개 채팅방으로 전달됨 — 대체발송 부적합`
                                : `카카오톡 ${kakao}개 채팅방 전달`
                            }
                          >
                            {kakao}회
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5">
                        {d.lastSharedChannel ? (
                          <span className="inline-flex items-center gap-1 text-gray-700">
                            <ShareChannelIcon
                              channel={d.lastSharedChannel}
                              className="h-3.5 w-3.5"
                            />
                            <span>
                              {shareChannelLabel[d.lastSharedChannel] ??
                                d.lastSharedChannel}
                            </span>
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {fmt(d.purchasedAt)}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-gray-500">
                        {fmt(d.expiresAt)}
                      </td>
                      <td className="max-w-[140px] px-3 py-1.5">
                        {d.memo ? (
                          <span className="block truncate text-gray-700" title={d.memo}>
                            {d.memo}
                          </span>
                        ) : (
                          <span className="text-gray-300">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !selected || (donors?.length ?? 0) === 0}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50"
          >
            {submitting ? '처리중…' : '대체발송 확정'}
          </button>
        </div>
      </div>
    </div>
  );
};
